import React, { useCallback, useEffect, useMemo, useState } from "react";
import { GridColDef } from "@mui/x-data-grid";
import BlockIcon from "@mui/icons-material/Block";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";

import { api } from "../../../../api/apiClient";
import FrontlineDataGrid from "../../../../components/common/FrontlineDataGrid";
import { notify } from "../../../components-ui/toast";
import { ServiceGroupDto } from "../../../types/types";

import "../../../../css/buttons.css";
import "../../../../css/cards.css";
import "../../../../css/forms.css";
import "../../../../css/tables.css";

interface ServiceGroupsCardProps {
  serviceId?: number;
  draftGroups?: DraftServiceGroup[];
  onDraftGroupsChange?: (groups: DraftServiceGroup[]) => void;
}

export type DraftServiceGroup = {
  clientId: number;
  name: string;
  description?: string | null;
  capacity?: number | null;
  isActive: boolean;
};

type ServiceGroupFormState = {
  name: string;
  description: string;
  capacity: string;
  isActive: boolean;
};

type ServiceGroupRow = ServiceGroupDto & {
  id: number;
  status: string;
  clientId?: number;
};

const emptyForm: ServiceGroupFormState = {
  name: "",
  description: "",
  capacity: "",
  isActive: true,
};

export default function ServiceGroupsCard({
  serviceId,
  draftGroups = [],
  onDraftGroupsChange,
}: ServiceGroupsCardProps) {
  const [groups, setGroups] = useState<ServiceGroupDto[]>([]);
  const [form, setForm] = useState<ServiceGroupFormState>(emptyForm);
  const [editingGroup, setEditingGroup] = useState<ServiceGroupDto | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const isDraftMode = !serviceId;

  const loadGroups = useCallback(async () => {
    if (!serviceId) return;

    try {
      setLoading(true);
      const result = await api<ServiceGroupDto[]>(
        `/api/services/${serviceId}/groups`,
      );
      setGroups(result ?? []);
    } catch (error) {
      console.error("Failed to load service groups", error);
      setGroups([]);
      notify.error("Failed to load groups/classes.");
    } finally {
      setLoading(false);
    }
  }, [serviceId]);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  const rows = useMemo<ServiceGroupRow[]>(
    () => {
      if (isDraftMode) {
        return draftGroups.map((group) => ({
          serviceGroupId: group.clientId,
          serviceId: 0,
          name: group.name,
          description: group.description,
          capacity: group.capacity,
          isActive: group.isActive,
          id: group.clientId,
          clientId: group.clientId,
          status: group.isActive ? "Active" : "Inactive",
        }));
      }

      return groups.map((group) => ({
        ...group,
        id: group.serviceGroupId,
        status: group.isActive ? "Active" : "Inactive",
      }));
    },
    [draftGroups, groups, isDraftMode],
  );

  const resetForm = () => {
    setForm(emptyForm);
    setEditingGroup(null);
    setShowForm(false);
  };

  const beginEdit = (group: ServiceGroupRow) => {
    setEditingGroup(group);
    setForm({
      name: group.name,
      description: group.description ?? "",
      capacity: group.capacity?.toString() ?? "",
      isActive: group.isActive,
    });
    setShowForm(true);
  };

  const saveGroup = async () => {
    const name = form.name.trim();
    if (!name) {
      notify.error("Group name is required.");
      return;
    }

    const capacity = form.capacity.trim()
      ? Number(form.capacity)
      : null;

    if (capacity !== null && (!Number.isInteger(capacity) || capacity < 0)) {
      notify.error("Capacity must be a whole number.");
      return;
    }

    const body = {
      name,
      description: form.description.trim() || null,
      capacity,
      isActive: form.isActive,
    };

    if (isDraftMode) {
      const nextGroup: DraftServiceGroup = {
        clientId: editingGroup?.serviceGroupId ?? Date.now(),
        ...body,
      };

      const nextGroups = editingGroup
        ? draftGroups.map((group) =>
            group.clientId === editingGroup.serviceGroupId ? nextGroup : group,
          )
        : [...draftGroups, nextGroup];

      onDraftGroupsChange?.(nextGroups);
      resetForm();
      return;
    }

    try {
      setSaving(true);
      if (editingGroup) {
        await api<ServiceGroupDto>(
          `/api/services/groups/${editingGroup.serviceGroupId}`,
          {
            method: "PUT",
            body,
          },
        );
        notify.success("Group/class updated.");
      } else {
        await api<ServiceGroupDto>(`/api/services/${serviceId}/groups`, {
          method: "POST",
          body,
        });
        notify.success("Group/class created.");
      }

      resetForm();
      await loadGroups();
    } catch (error) {
      console.error("Failed to save service group", error);
      notify.error("Failed to save group/class.");
    } finally {
      setSaving(false);
    }
  };

  const deactivateGroup = async (group: ServiceGroupRow) => {
    if (isDraftMode) {
      onDraftGroupsChange?.(
        draftGroups.filter((draftGroup) => draftGroup.clientId !== group.id),
      );
      return;
    }

    try {
      setSaving(true);
      await api<void>(`/api/services/groups/${group.serviceGroupId}`, {
        method: "DELETE",
      });
      notify.success("Group/class deactivated.");
      await loadGroups();
    } catch (error) {
      console.error("Failed to deactivate service group", error);
      notify.error("Failed to deactivate group/class.");
    } finally {
      setSaving(false);
    }
  };

  const columns: GridColDef<ServiceGroupRow>[] = [
    { field: "name", headerName: "Name", flex: 1, minWidth: 180 },
    {
      field: "description",
      headerName: "Description",
      flex: 1.5,
      minWidth: 220,
      valueGetter: (params) => params.row.description ?? "",
    },
    {
      field: "capacity",
      headerName: "Capacity",
      width: 120,
      valueGetter: (params) => params.row.capacity ?? "",
    },
    {
      field: "status",
      headerName: "Status",
      width: 130,
      renderCell: (params) => (
        <span
          className={`frontline-grid-status ${
            params.row.isActive
              ? "frontline-grid-status--active"
              : "frontline-grid-status--inactive"
          }`}
        >
          {params.row.status}
        </span>
      ),
    },
    {
      field: "actions",
      headerName: "",
      width: 110,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <div className="frontline-grid-actions">
          <button
            type="button"
            className="icon-button"
            title="Edit group/class"
            onClick={() => beginEdit(params.row)}
            disabled={saving}
          >
            <EditIcon fontSize="small" />
          </button>
          {params.row.isActive && (
            <button
              type="button"
              className="icon-button"
              title="Deactivate group/class"
              onClick={() => deactivateGroup(params.row)}
              disabled={saving}
            >
              <BlockIcon fontSize="small" />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="card-header-row">
        <div>
          <div className="card-title">Groups / Classes</div>
        </div>

        <div className="table-actions">
          <button
            type="button"
            className="link-button link-button-with-icon card-header-link-action"
            onClick={() => {
              if (showForm && !editingGroup) {
                resetForm();
                return;
              }

              setEditingGroup(null);
              setForm(emptyForm);
              setShowForm(true);
            }}
            disabled={saving}
          >
            {showForm && !editingGroup ? (
              <CloseIcon fontSize="small" />
            ) : (
              <EditIcon fontSize="small" />
            )}
            {showForm && !editingGroup ? "Cancel" : "Add Group/Class"}
          </button>
        </div>
      </div>

      {showForm && (
        <div className="form-row" style={{ alignItems: "end" }}>
          <div className="form-group">
            <label className="form-label">Name</label>
            <input
              className="form-input"
              value={form.name}
              onChange={(event) =>
                setForm((value) => ({ ...value, name: event.target.value }))
              }
              disabled={saving}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <input
              className="form-input"
              value={form.description}
              onChange={(event) =>
                setForm((value) => ({
                  ...value,
                  description: event.target.value,
                }))
              }
              disabled={saving}
            />
          </div>
          <div className="form-group" style={{ maxWidth: 140 }}>
            <label className="form-label">Capacity</label>
            <input
              className="form-input"
              type="number"
              min="0"
              value={form.capacity}
              onChange={(event) =>
                setForm((value) => ({ ...value, capacity: event.target.value }))
              }
              disabled={saving}
            />
          </div>
          {editingGroup && (
            <label className="option-item" style={{ marginBottom: 8 }}>
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(event) =>
                  setForm((value) => ({
                    ...value,
                    isActive: event.target.checked,
                  }))
                }
                disabled={saving}
              />
              Active
            </label>
          )}
          <div className="button-row button-row-left" style={{ marginTop: 0 }}>
            <button
              type="button"
              className="action-button compact"
              onClick={saveGroup}
              disabled={saving}
            >
              Save
            </button>
            <button
              type="button"
              className="action-button compact secondary"
              onClick={resetForm}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="table-container">
        <FrontlineDataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          localeText={{
            noRowsLabel: "No groups configured for this service",
          }}
        />
      </div>
    </div>
  );
}
